/**
 * Draggable Sidebar Post Group Component
 *
 * Post card with nested variations that can be expanded/collapsed
 * - Main post card (draggable)
 * - Nested variations (draggable individually)
 * - Expand/collapse for variations
 * - Status badges for both post and variations
 */

import { useDraggable } from '@dnd-kit/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, RetryImage, Icon, PlaceholderImage } from '@/components/ui';
import { SelectChevron } from '@/components/ui/Select';
import { Post, PostVariationSummary } from '@/services/posts';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { getPostStatusBadge, getVariationStatusBadge } from '@/utils/calendarStatus';
import { releaseItemKey } from '@/types/releaseSections';

/** Small "New" pill for newly released items (calendar nudge). */
const NewBadge = ({ label }: { label: string }) => (
  <span className="inline-flex flex-shrink-0 items-center rounded bg-coral-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-coral-700">
    {label}
  </span>
);

interface DraggableSidebarPostGroupProps {
  post: Post;
  isSelected: boolean;
  selectedVariationId?: number;
  onClick: () => void;
  onVariationClick?: (variation: PostVariationSummary) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onEditVariation?: (variation: PostVariationSummary) => void;
  onDeleteVariation?: (variation: PostVariationSummary) => void;
  /** Exact items released since the last visit (`<postId>:base` / `<postId>:<variationId>`) */
  newItemKeys?: ReadonlySet<string>;
}

export const DraggableSidebarPostGroup = ({
  post,
  isSelected,
  selectedVariationId,
  onClick,
  onVariationClick,
  onEdit,
  onDelete,
  onEditVariation,
  onDeleteVariation,
  newItemKeys,
}: DraggableSidebarPostGroupProps) => {
  const { t } = useTranslation('common');
  const { t: tCal } = useTranslation('calendar');

  // Expand/collapse state: default collapsed
  const hasVariations = (post.variations?.length ?? 0) > 0;
  const variationsCount = post.variations?.length ?? 0;
  const isBaseNew = newItemKeys?.has(releaseItemKey(post.id, null)) ?? false;
  const hasNewVariation =
    post.variations?.some((variation) => newItemKeys?.has(releaseItemKey(post.id, variation.id))) ?? false;
  const [isExpanded, setIsExpanded] = useState(hasNewVariation);
  // A newly released version must be visible, not hidden behind the collapsed list
  useEffect(() => {
    if (hasNewVariation) setIsExpanded(true);
  }, [hasNewVariation]);

  // Get first media item if available
  const firstMedia = post.postMedia?.[0];
  const hasMedia = firstMedia && firstMedia.originalUrl;

  /**
   * Get content status badge (DRAFT or READY)
   * Matches Creator page PostTable style
   */
  const getContentStatusBadge = (
    postData: Post
  ): { color: string; bgColor: string; label: string; emoji: string } | null => {
    if (postData.contentStatus === 'DRAFT') {
      return {
        color: 'text-amber-700',
        bgColor: 'bg-amber-100',
        label: t('draft', { defaultValue: 'Draft' }),
        emoji: '✏️',
      };
    }
    if (postData.contentStatus === 'READY') {
      return {
        color: 'text-green-700',
        bgColor: 'bg-green-100',
        label: t('ready_to_shoot', { defaultValue: 'Ready to Shoot' }),
        emoji: '🚀',
      };
    }
    return null;
  };

  /**
   * Get variation content status badge
   */
  const getVariationContentStatusBadge = (
    variation: PostVariationSummary
  ): { color: string; bgColor: string; label: string; emoji: string } | null => {
    if (variation.contentStatus === 'DRAFT') {
      return {
        color: 'text-amber-700',
        bgColor: 'bg-amber-100',
        label: t('draft', { defaultValue: 'Draft' }),
        emoji: '✏️',
      };
    }
    if (variation.contentStatus === 'READY') {
      return {
        color: 'text-green-700',
        bgColor: 'bg-green-100',
        label: t('ready_to_shoot', { defaultValue: 'Ready to Shoot' }),
        emoji: '🚀',
      };
    }
    return null;
  };

  const contentStatus = getContentStatusBadge(post);
  const inferredStatus = getPostStatusBadge(post, t);

  // Post can always be dragged (same rules as DraggableSidebarPost)
  const canDrag = true;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar-post-${post.id}`,
    data: { type: 'sidebar-post', post },
    disabled: !canDrag,
  });

  return (
    <div className="relative">
      {/* Main Post Card */}
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onClick={onClick}
        className={`
          group relative p-3 border-b border-gray-200
          cursor-grab active:cursor-grabbing
          hover:bg-gray-50 transition-colors
          ${isSelected ? 'bg-primary-50 border-l-2 border-l-primary-500' : ''}
          ${isDragging ? 'opacity-50' : ''}
        `}
        role="button"
        tabIndex={0}
        aria-label={`Post: ${post.caption.substring(0, 50)}${post.caption.length > 50 ? '...' : ''}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
      >
        <div className="flex gap-3">
          {/* Thumbnail */}
          <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
            {hasMedia ? (
              <RetryImage
                src={firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <PlaceholderImage />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Caption */}
            <p className="text-sm text-gray-900 line-clamp-2 mb-1 whitespace-pre-wrap">{post.caption || 'No caption'}</p>

            {/* Status badges + Variations indicator */}
            <div className="flex items-center gap-1.5 mt-1">
              {isBaseNew && <NewBadge label={tCal('release_new_badge')} />}
              {/* Content status badge (DRAFT or READY) - emoji only */}
              {contentStatus && (
                <Tooltip content={contentStatus.label}>
                  <span
                    className={cn(
                      'w-6 h-6 flex items-center justify-center text-sm rounded',
                      contentStatus.bgColor
                    )}
                  >
                    {contentStatus.emoji}
                  </span>
                </Tooltip>
              )}

              {/* Inferred status badge (MAPPED, SCHEDULED, PUBLISHED, FAILED, or Unmapped) - icon only */}
              {inferredStatus ? (
                <Tooltip content={inferredStatus.label}>
                  <span
                    className={cn(
                      'w-6 h-6 flex items-center justify-center rounded',
                      inferredStatus.bgColor
                    )}
                  >
                    <Icon name={inferredStatus.icon} size="sm" className={inferredStatus.color} ariaHidden />
                  </span>
                </Tooltip>
              ) : (
                <Tooltip content={t('status_unmapped', { defaultValue: 'Not on calendar' })}>
                  <span className="w-6 h-6 flex items-center justify-center rounded bg-gray-100">
                    <Icon name={ICONS.CALENDAR} size="sm" className="text-gray-400" ariaHidden />
                  </span>
                </Tooltip>
              )}

              {/* Variations count/expand button */}
              {hasVariations && (
                <Tooltip content={isExpanded ? t('collapse_variations', { defaultValue: 'Click to collapse variations' }) : t('expand_variations', { defaultValue: 'Click to expand variations' })}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(!isExpanded);
                    }}
                    className="ml-auto flex items-center gap-1 px-2 py-1 text-xs font-medium text-secondary-600 border border-secondary-300 rounded hover:border-secondary-400 hover:text-secondary-700 transition-colors"
                    aria-label={isExpanded ? t('collapse_variations') : t('expand_variations')}
                    aria-expanded={isExpanded}
                  >
                    <SelectChevron className={cn('shrink-0 transition-transform', !isExpanded && '-rotate-90')} />
                    <span>
                      {variationsCount} {t('variations', { count: variationsCount, defaultValue: 'variations' })}
                    </span>
                    {hasNewVariation && <NewBadge label={tCal('release_new_badge')} />}
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons - top right, appear on hover */}
        {(onEdit || onDelete) && (
          <div className="absolute top-2 right-2 hidden group-hover:flex items-center gap-1 bg-white rounded-md shadow-sm border border-gray-200 p-0.5">
            {onEdit && (
              <Tooltip content={t('edit')}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  className="w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                  aria-label={t('edit')}
                >
                  <Icon name={ICONS.EDIT} size="sm" ariaHidden />
                </button>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip content={t('delete')}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  className="w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                  aria-label={t('delete')}
                >
                  <Icon name={ICONS.TRASH} size="sm" ariaHidden />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      {/* Nested Variations (expanded) */}
      {hasVariations && isExpanded && (
        <div className="border-l-2 border-secondary-400 pl-4 bg-gray-50">
          {post.variations?.map((variation) => (
            <VariationCard
              key={variation.id}
              variation={variation}
              postId={post.id}
              postCaption={post.caption}
              postMedia={post.postMedia}
              isSelected={selectedVariationId === variation.id}
              isNew={newItemKeys?.has(releaseItemKey(post.id, variation.id)) ?? false}
              onClick={() => onVariationClick?.(variation)}
              onEdit={() => onEditVariation?.(variation)}
              onDelete={onDeleteVariation && (post.variations?.length ?? 0) > 1 ? () => onDeleteVariation(variation) : undefined} // a post's only variation can't be deleted
              getContentStatusBadge={getVariationContentStatusBadge}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Individual Variation Card (nested inside post group)
 */
interface VariationCardProps {
  variation: PostVariationSummary;
  postId: number;
  postCaption: string;
  postMedia?: { id: number; type: string; originalUrl: string; thumbnailUrl: string | null }[];
  isSelected: boolean;
  isNew: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  getContentStatusBadge: (
    variation: PostVariationSummary
  ) => { color: string; bgColor: string; label: string; emoji: string } | null;
}

const VariationCard = ({
  variation,
  postId,
  postCaption,
  postMedia,
  isSelected,
  isNew,
  onClick,
  onEdit,
  onDelete,
  getContentStatusBadge,
}: VariationCardProps) => {
  const { t } = useTranslation('common');
  const { t: tCal } = useTranslation('calendar');

  // Get effective media: variation's own media first, fall back to parent post's media
  const effectiveMedia =
    variation.postMedia && variation.postMedia.length > 0 ? variation.postMedia : postMedia;
  const firstMedia = effectiveMedia?.[0];
  const hasMedia = firstMedia && firstMedia.originalUrl;

  // Get effective caption: variation's caption or parent post's caption
  const effectiveCaption = variation.caption || postCaption || '';

  const contentStatus = getContentStatusBadge(variation);
  const inferredStatus = getVariationStatusBadge(variation, t);

  // Variation can always be dragged
  const canDrag = true;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar-variation-${variation.id}`,
    data: {
      type: 'sidebar-variation',
      variation: { ...variation, postId },
      postId,
    },
    disabled: !canDrag,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      className={`
        group relative p-2 border-b border-gray-200
        cursor-grab active:cursor-grabbing
        hover:bg-gray-100 transition-colors
        ${isSelected ? 'bg-secondary-50 border-l-2 border-l-secondary-500' : ''}
        ${isDragging ? 'opacity-50' : ''}
      `}
      role="button"
      tabIndex={0}
      aria-label={`Variation: ${variation.title} - ${effectiveCaption.substring(0, 50)}${effectiveCaption.length > 50 ? '...' : ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="flex gap-2">
        {/* Thumbnail (smaller, compact) */}
        <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden flex-shrink-0 relative">
          {hasMedia ? (
            <RetryImage
              src={firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <PlaceholderImage />
          )}
          {/* Variation indicator overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-secondary-600/90 text-secondary-600-contrast text-[10px] font-medium text-center py-0.5 truncate px-1">
            {variation.title}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Variation title badge */}
          <div className="flex items-center gap-1 mb-1">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border border-secondary-300 text-secondary-600">
              <Icon name={ICONS.COPY} size="xs" ariaHidden />
              {variation.title}
            </span>
            {isNew && <NewBadge label={tCal('release_new_badge')} />}
          </div>

          {/* Caption (compact, 1 line) */}
          <p className="text-xs text-gray-700 line-clamp-1 mb-1 whitespace-pre-wrap">{effectiveCaption || 'No caption'}</p>

          {/* Status badges */}
          <div className="flex items-center gap-1">
            {/* Content status badge (DRAFT or READY) */}
            {contentStatus && (
              <Tooltip content={contentStatus.label}>
                <span
                  className={cn(
                    'w-5 h-5 flex items-center justify-center text-xs rounded',
                    contentStatus.bgColor
                  )}
                >
                  {contentStatus.emoji}
                </span>
              </Tooltip>
            )}

            {/* Inferred status badge (MAPPED, SCHEDULED, PUBLISHED, FAILED, or Unmapped) */}
            {inferredStatus ? (
              <Tooltip content={inferredStatus.label}>
                <span
                  className={cn('w-5 h-5 flex items-center justify-center rounded', inferredStatus.bgColor)}
                >
                  <Icon name={inferredStatus.icon} size="xs" className={inferredStatus.color} ariaHidden />
                </span>
              </Tooltip>
            ) : (
              <Tooltip content={t('status_unmapped', { defaultValue: 'Not on calendar' })}>
                <span className="w-5 h-5 flex items-center justify-center rounded bg-gray-100">
                  <Icon name={ICONS.CALENDAR} size="xs" className="text-gray-400" ariaHidden />
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons - top right, appear on hover */}
      {(onEdit || onDelete) && (
        <div className="absolute top-1 right-1 hidden group-hover:flex items-center gap-0.5 bg-white rounded-md shadow-sm border border-gray-200 p-0.5">
          {onEdit && (
            <Tooltip content={t('edit')}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="w-5 h-5 flex items-center justify-center rounded text-gray-500 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                aria-label={t('edit')}
              >
                <Icon name={ICONS.EDIT} size="xs" ariaHidden />
              </button>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip content={t('delete')}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="w-5 h-5 flex items-center justify-center rounded text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                aria-label={t('delete')}
              >
                <Icon name={ICONS.TRASH} size="xs" ariaHidden />
              </button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};
